import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import ChatMessages from './ChatMessages';
import ChatForm from './ChatForm';
import type { UiMessage } from './types';
import {
  type ChatArtifact,
  fetchConversationMessages,
  fetchConversations,
  normalizePersistedMessageArtifact,
  postChatMessage,
  type ConversationSummary,
  type MessageOut,
} from '../../api/chat';

const HEADER_PURPLE = '#7c3aed';

function formatChatApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: unknown } | undefined;
    if (data?.detail !== undefined) {
      const d = data.detail;
      if (typeof d === 'string') return d;
      if (Array.isArray(d))
        return d
          .map((item) =>
            typeof item === 'object' && item && 'msg' in item
              ? String((item as { msg: unknown }).msg)
              : JSON.stringify(item),
          )
          .join('; ');
    }
    return err.message || 'Request failed';
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Please try again.';
}

function mapApiMessagesToUi(messages: MessageOut[]): UiMessage[] {
  return messages.map((msg) => ({
    id: `api-${msg.id}`,
    kind: 'text' as const,
    role: msg.role === 'user' ? 'user' : 'assistant',
    content: msg.content,
    artifact: normalizePersistedMessageArtifact(msg.artifact ?? undefined),
  }));
}

type ChatContainerProps = {
  onClose?: () => void;
  isOpen?: boolean;
  title?: string;
  showCloseButton?: boolean;
  pageMode?: boolean;
  showHeader?: boolean;
};

export default function ChatContainer({
  onClose,
  isOpen = true,
  title = 'Live chat',
  showCloseButton = true,
  pageMode = false,
  showHeader = true,
}: ChatContainerProps) {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const prevOpenRef = useRef(false);

  const loadConversationList = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const list = await fetchConversations();
      setConversations(list);
    } catch {
      setConversations([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const openConversation = useCallback(async (id: number, closeHistory = true) => {
    setLoadingConversation(true);
    if (closeHistory) setHistoryOpen(false);
    try {
      const detail = await fetchConversationMessages(id);
      setConversationId(detail.id ?? id);
      setMessages(mapApiMessagesToUi(detail.messages ?? []));
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `load-err-${Date.now()}`,
          kind: 'text',
          role: 'assistant',
          content: 'Could not load that conversation.',
          isError: true,
        },
      ]);
    } finally {
      setLoadingConversation(false);
    }
  }, []);

  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (!justOpened) return;

    void (async () => {
      setLoadingHistory(true);
      try {
        const list = await fetchConversations();
        setConversations(list);

        const latest = list[0];
        const hasLoadedMessages = messages.length > 0;
        if (!hasLoadedMessages && latest?.id) {
          await openConversation(latest.id, false);
        }
      } catch {
        setConversations([]);
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [isOpen, messages.length, openConversation]);

  useEffect(() => {
    if (historyOpen && !loadingHistory && conversations.length === 0) {
      void loadConversationList();
    }
  }, [historyOpen, loadingHistory, conversations.length, loadConversationList]);

  const appendMessage = (msg: UiMessage) => {
    setMessages((prev) => [...prev, msg]);
  };

  const removeTyping = (typingId: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== typingId));
  };

  const finalizeAssistantTextMessage = (
    messageId: string,
    content: string,
    opts: {
      isError?: boolean;
      artifact?: ChatArtifact;
      followUpSuggestions?: string[];
    },
  ) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId && m.kind === 'text'
          ? {
              ...m,
              content,
              artifact: opts.artifact,
              followUpSuggestions: opts.followUpSuggestions,
              isError: opts.isError ?? false,
              isStreaming: false,
            }
          : m,
      ),
    );
  };

  const handleSend = async (text: string) => {
    const userMsg: UiMessage = {
      id: `user-${Date.now()}`,
      kind: 'text',
      role: 'user',
      content: text,
    };
    appendMessage(userMsg);
    const typingId = `typing-${Date.now()}`;
    appendMessage({ id: typingId, kind: 'typing' });
    setAwaitingReply(true);
    const streamingAssistantId = `assistant-stream-${Date.now()}`;
    const streamStartedRef = { current: false };

    try {
      const res = await postChatMessage(
        {
          query: text,
          conversation_id: conversationId,
        },
        {
          onPartialAnswer: (delta) => {
            setMessages((prev) => {
              if (!streamStartedRef.current) {
                streamStartedRef.current = true;
                const withoutTyping = prev.filter((m) => m.id !== typingId);
                return [
                  ...withoutTyping,
                  {
                    id: streamingAssistantId,
                    kind: 'text' as const,
                    role: 'assistant' as const,
                    content: delta,
                    isStreaming: true,
                  },
                ];
              }
              return prev.map((m) =>
                m.id === streamingAssistantId && m.kind === 'text'
                  ? { ...m, content: `${m.content}${delta}` }
                  : m,
              );
            });
          },
        },
      );
      setConversationId(res.conversation_id);
      removeTyping(typingId);

      let body = res.response;
      if (res.permission_denied) {
        const denied = res.denied_modules?.length
          ? ` (modules: ${res.denied_modules.join(', ')})`
          : '';
        body = `You do not have permission to access that data${denied}.`;
      }
      if (streamStartedRef.current) {
        finalizeAssistantTextMessage(streamingAssistantId, body, {
          isError: res.permission_denied,
          artifact: res.artifact,
          followUpSuggestions: res.follow_up_suggestions,
        });
      } else {
        appendMessage({
          id: `assistant-${res.message_id}`,
          kind: 'text',
          role: 'assistant',
          content: body,
          artifact: res.artifact,
          followUpSuggestions: res.follow_up_suggestions,
          isError: res.permission_denied,
          isStreaming: false,
        });
      }
    } catch (err: unknown) {
      removeTyping(typingId);
      const errText = formatChatApiError(err);
      if (streamStartedRef.current) {
        finalizeAssistantTextMessage(streamingAssistantId, errText, {
          isError: true,
        });
      } else {
        appendMessage({
          id: `err-${Date.now()}`,
          kind: 'text',
          role: 'assistant',
          content: errText,
          isError: true,
        });
      }
    } finally {
      setAwaitingReply(false);
    }
  };

  const startNewChat = () => {
    setConversationId(null);
    setMessages([]);
    setHistoryOpen(false);
  };

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#fff',
        borderRadius: 0,
        overflow: 'hidden',
        border: 'none',
      }}
    >
      {showHeader ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: { xs: 1, sm: 1.25, md: 1.5 },
            py: { xs: 1, sm: 1.15, md: 1.25 },
            bgcolor: pageMode ? '#ffffff' : HEADER_PURPLE,
            color: pageMode ? '#0f172a' : '#fff',
            flexShrink: 0,
            minHeight: { xs: 48, sm: 50, md: 52 },
            fontSize: { xs: 12, sm: 12.5, md: 13 },
            borderBottom: pageMode ? '1px solid #e2e8f0' : 'none',
          }}
        >
          <Box
            sx={{
              width: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <IconButton
              size="small"
              onClick={() => setHistoryOpen((v) => !v)}
              sx={{ color: pageMode ? '#334155' : '#fff', p: 0.5 }}
              aria-label={historyOpen ? 'Back to chat' : 'Conversation history'}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: pageMode
                ? { xs: 14, sm: 15, md: 16, lg: 17 }
                : { xs: 12, sm: 12.5, md: 13, lg: 13.5 },
              fontWeight: 700,
              pb: 0.5,
              textAlign: 'center',
              lineHeight: 1.25,
              px: { xs: 0.5, sm: 0 },
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              width: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {showCloseButton ? (
              <IconButton
                size="small"
                onClick={onClose}
                sx={{ color: pageMode ? '#334155' : '#fff', p: 0.5 }}
                aria-label="Close chat"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
        </Box>
      ) : null}

      <Collapse in={historyOpen}>
        <Box
          sx={{
            maxHeight: 160,
            flexShrink: 0,
            overflowY: 'auto',
            borderBottom: '1px solid #eee',
            px: 1,
            py: 0.5,
          }}
        >
          <List dense disablePadding>
            <ListItemButton onClick={startNewChat} sx={{ borderRadius: 1 }}>
              <ListItemText
                primary="New conversation"
                primaryTypographyProps={{ fontSize: 12, fontWeight: 600 }}
              />
            </ListItemButton>
            {loadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              conversations.map((c) => (
                <ListItemButton
                  key={c.id}
                  onClick={() => void openConversation(c.id)}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={c.title || `Conversation ${c.id}`}
                    secondary={
                      c.last_message_at
                        ? new Date(c.last_message_at).toLocaleString()
                        : undefined
                    }
                    primaryTypographyProps={{
                      fontSize: 11,
                      noWrap: true,
                    }}
                    secondaryTypographyProps={{ fontSize: 9 }}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </Collapse>

      {loadingConversation ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={28} sx={{ color: HEADER_PURPLE }} />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ChatMessages
            messages={messages}
            onFollowUpClick={(query) => {
              void handleSend(query);
            }}
            disableFollowUps={awaitingReply}
          />
          <ChatForm disabled={awaitingReply} onSend={(t) => void handleSend(t)} />
        </Box>
      )}
    </Box>
  );
}
